import axios from 'axios';
import config from '../config';

/**
 * Regular API instance for standard requests
 */
const api = axios.create({
  baseURL: config.API.BACKEND_URL,
  timeout: config.TIMEOUTS.DEFAULT, // Updated to use config timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API instance specifically for AI operations with longer timeout
 * DeepSeek API calls are taking 50-55 seconds based on logs
 */
const aiApi = axios.create({
  baseURL: config.API.BACKEND_URL,
  timeout: config.TIMEOUTS.AI_OPERATIONS, // Updated to use config timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API instance specifically for blog operations
 */
const blogApi = axios.create({
  baseURL: config.API.BACKEND_URL,
  timeout: config.TIMEOUTS.BLOG_OPERATIONS, // Using blog timeout from config
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for API calls
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Apply the same interceptors to aiApi
aiApi.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Apply the same interceptors to blogApi
blogApi.interceptors.request.use(
  (config) => {
    if (config.url && !config.url.includes('http')) {
      // API request initiated
    }
    return config;
  },
  (error) => {
    console.error('Blog API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
const responseInterceptor = (response) => {
  return response;
};

const errorInterceptor = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const status = error.response.status;
    const data = error.response.data;

    console.error(`API Error (${status}):`, data);

    // Handle specific error cases
    if (status === 401) {
      // Unauthorized - session may have expired
      console.error('Authentication error. User may need to log in again.');
      // You could dispatch an event or action here to handle session expiry
    } else if (status === 403) {
      console.error('Permission denied. User may need to upgrade membership.');
    } else if (status === 429) {
      console.error('Rate limit exceeded. Too many requests.');
    }
  } else if (error.request) {
    // The request was made but no response was received
    console.error('API No Response Error:', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('API Setup Error:', error.message);
  }

  return Promise.reject(error);
};

api.interceptors.response.use(responseInterceptor, errorInterceptor);
aiApi.interceptors.response.use(responseInterceptor, errorInterceptor);
blogApi.interceptors.response.use(responseInterceptor, errorInterceptor);

// Helper function to try multiple endpoints until one works
const tryMultipleEndpoints = async (endpoints, axiosInstance, options = {}) => {
  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      console.log(`Trying endpoint: ${endpoint}`);
      const response = await axiosInstance.get(endpoint, options);
      console.log(`Successful response from endpoint: ${endpoint}`);
      return response;
    } catch (error) {
      console.warn(`Endpoint ${endpoint} failed: ${error.message}`);
      lastError = error;
      // Continue to the next endpoint
    }
  }

  // All endpoints failed
  throw lastError || new Error('All endpoints failed');
};

// Cache to prevent duplicate API calls for the same VIN during a session
const vehicleDataCache = new Map();

/**
 * API service with methods for different endpoints
 */
const apiService = {
  /**
   * VIN lookup using unified backend endpoint with Firebase token authentication
   * @param {string} vin - The vehicle identification number
   * @param {string} userId - Optional user ID for tracking searches
   * @returns {Promise} API response with vehicle data
   */
  getVehicleData: async (vin, userId = '', bypassCache = false) => {
    // Use in-memory cache for quick repeated lookups (for the same session)
    const cacheKey = `${vin}_${userId}`;

    // Return from cache if available and not bypassing cache
    if (!bypassCache && vehicleDataCache.has(cacheKey)) {
      console.log(`Using cached data for VIN: ${vin}`);
      return vehicleDataCache.get(cacheKey);
    }

    // SECURITY FIX: Include Firebase token in request headers
    let headers = {
      'Content-Type': 'application/json',
    };

    try {
      // Get Firebase auth instance and current user token
      const { auth } = await import('../firebase');
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken();
        headers.Authorization = `Bearer ${idToken}`;
        console.log(
          `Including Firebase token for authenticated request: VIN ${vin}`
        );
      } else {
        console.log(
          `No authenticated user - making unauthenticated request: VIN ${vin}`
        );
      }
    } catch (error) {
      console.warn('Failed to get Firebase token:', error.message);
      // Continue without token - server will handle as free tier request
    }

    // Not in cache or bypassing cache, make the API call
    console.log(`Making fresh API call for VIN: ${vin}, userId: ${userId}`);
    const response = await api.get(config.API.ENDPOINTS.VIN_LOOKUP, {
      params: { vin, cached: !bypassCache },
      headers,
    });

    // Cache the response for future use
    vehicleDataCache.set(cacheKey, response);

    return response;
  },

  /**
   * Legacy ClearVIN API endpoint (kept for backward compatibility)
   * @param {string} vin - The vehicle identification number
   * @param {string} userId - Optional user ID for tracking searches
   * @returns {Promise} API response with vehicle data
   */
  getClearVinData: (vin, userId = '') => {
    // Use the main getVehicleData method to prevent duplicate API calls
    console.log(
      `getClearVinData: Delegating to getVehicleData for VIN: ${vin}`
    );
    return apiService.getVehicleData(vin, userId);
  },

  /**
   * Get fresh vehicle data (bypass cache) - for when users explicitly want updated data
   * @param {string} vin - The vehicle identification number
   * @param {string} userId - Optional user ID for tracking searches
   * @returns {Promise} API response with fresh vehicle data
   */
  getFreshVehicleData: (vin, userId = '') => {
    console.log(
      `getFreshVehicleData: Requesting fresh data for VIN: ${vin} (bypassing cache)`
    );
    return apiService.getVehicleData(vin, userId, true); // true = bypassCache
  },

  /**
   * AI analysis of vehicle data - using aiApi with longer timeout
   * @param {object} vehicleData - Vehicle data object to analyze
   * @param {string} idToken - Firebase authentication token
   * @param {boolean} useDeepSeek - Whether to use DeepSeek API
   * @returns {Promise} AI analysis of vehicle
   */
  analyzeVehicle: (
    vehicleData,
    idToken = null,
    useDeepSeek = true,
    processedData = null
  ) => {
    return aiApi.post(config.API.ENDPOINTS.AI.ANALYZE, {
      vehicleData,
      idToken,
      useDeepSeek,
      processedData,
    });
  },

  /**
   * AI chat functionality - using aiApi with longer timeout
   * @param {string} message - User message
   * @param {object} vehicleContext - Optional vehicle context data
   * @param {array} chatHistory - Previous chat messages
   * @param {string} idToken - Firebase authentication token
   * @param {array} relevantBlogPosts - Optional relevant blog posts for context
   * @returns {Promise} AI response
   */
  aiChat: (
    message,
    vehicleContext = null,
    chatHistory = [],
    idToken = null,
    relevantBlogPosts = [],
    processedData = null
  ) => {
    return aiApi.post(config.API.ENDPOINTS.AI.CHAT, {
      message,
      vehicleContext,
      chatHistory,
      idToken,
      useDeepSeek: config.FEATURES.USE_DEEP_SEEK,
      relevantBlogPosts,
      processedData,
    });
  },

  /**
   * Get AI search tip
   * @param {string} userRole - User role/membership level
   * @returns {Promise} AI tip for search
   */
  getSearchTip: (userRole = 'free') => {
    return api.post(config.API.ENDPOINTS.AI.SEARCH_TIP, { userRole });
  },

  /**
   * Get free AI suggestion
   * @param {object} vehicleContext - Vehicle context if available
   * @param {string} userActivity - Current user activity
   * @param {object} pageInfo - Current page information
   * @param {array} relevantBlogPosts - Optional relevant blog posts for context
   * @returns {Promise} AI suggestion
   */
  getFreeSuggestion: (
    vehicleContext = null,
    userActivity = 'browsing',
    pageInfo = null,
    relevantBlogPosts = []
  ) => {
    return api.post(config.API.ENDPOINTS.AI.FREE_SUGGESTION, {
      vehicleContext,
      userActivity,
      pageInfo,
      relevantBlogPosts,
    });
  },

  /**
   * Vini chat — site-wide AI assistant for free and premium general questions
   * @param {string} message - User message
   * @param {array} chatHistory - Previous messages in this session
   * @param {object} pageContext - Current page context
   * @param {array} blogPosts - Real blog posts fetched from Firestore
   * @param {string} userTier - 'free' | 'premium' | 'business' | 'demo'
   * @param {array} garageVehicles - User's garage vehicles (if any)
   * @param {string|null} idToken - Firebase auth token (optional)
   * @returns {Promise} Vini response
   */
  viniChat: (
    message,
    chatHistory = [],
    pageContext = null,
    blogPosts = [],
    userTier = 'free',
    garageVehicles = [],
    idToken = null
  ) => {
    return api.post(config.API.ENDPOINTS.AI.VINI_CHAT, {
      message,
      chatHistory,
      pageContext,
      blogPosts,
      userTier,
      garageVehicles,
      idToken,
    });
  },

  /**
   * Compare multiple vehicles using AI analysis - using aiApi with longer timeout
   * @param {Object} data - The comparison data
   * @param {Array} data.vehicles - Array of vehicle objects to compare
   * @param {string} [data.idToken] - Firebase auth token
   * @returns {Promise<Object>} The AI comparison results
   */
  compareVehicles: async function (data) {
    const { vehicles, idToken } = data;

    console.log('apiService.compareVehicles called with:', {
      vehiclesCount: vehicles?.length,
      hasToken: !!idToken,
      tokenPreview: idToken ? `${idToken.substring(0, 10)}...` : 'No token',
      timestamp: new Date().toISOString(),
    });

    try {
      // Start with the default headers from aiApi and merge with authorization
      const requestConfig = {
        headers: {
          'Content-Type': 'application/json', // Ensure this is set
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
        },
      };

      console.log(
        'Request config headers:',
        Object.keys(requestConfig.headers)
      );

      if (idToken) {
        console.log(
          'Authorization header added:',
          requestConfig.headers.Authorization ? 'Yes' : 'No'
        );
      } else {
        console.log('No idToken provided to compareVehicles method');
      }

      const response = await aiApi.post(
        config.API.ENDPOINTS.AI.COMPARE_VEHICLES,
        { vehicles },
        requestConfig
      );

      console.log('compareVehicles API response received:', {
        status: response.status,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      return response;
    } catch (error) {
      console.error('API error in compareVehicles:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  },

  /**
   * Direct NHTSA API access (only used for free tier)
   * @param {string} vin - The vehicle identification number
   * @returns {Promise} NHTSA data response
   */
  getNHTSAData: (vin) => {
    return axios.get(
      `${config.EXTERNAL_APIS.NHTSA_URL}/decodevin/${vin}?format=json`
    );
  },

  /**
   * Get free NHTSA vehicle data with basic analysis (no auth required)
   * @param {string} vin - The vehicle identification number
   * @returns {Promise} Enhanced NHTSA data with basic analysis
   */
  getFreeNHTSAData: (vin) => {
    console.log(`🆓 Fetching free NHTSA data for VIN: ${vin}`);
    return api.get(`/api/nhtsa/vin/${vin}`);
  },

  /**
   * Get vehicle report from Firebase instead of calling ClearVIN API again
   * @param {string} vin - The vehicle identification number
   * @param {string} token - Authorization token (optional for Firebase access)
   * @returns {Promise} Vehicle report response from Firebase
   */
  getVehicleReport: async (vin, token = '') => {
    console.log(`🔍 Fetching vehicle report from Firebase for VIN: ${vin}`);

    try {
      // Import Firebase dependencies (you might need to adjust imports)
      const { auth, db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');

      // Get current user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be logged in to access vehicle reports');
      }

      console.log(`📱 Getting user document for: ${currentUser.uid}`);

      // Get user document from Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      const garage = userData.garage || [];

      console.log(`🚗 Found ${garage.length} vehicles in user's garage`);

      // Find the vehicle with matching VIN
      const vehicle = garage.find((v) => v.vin === vin);

      if (!vehicle) {
        console.warn(`❌ Vehicle with VIN ${vin} not found in garage`);
        // If not found in garage, check recent searches
        const recentSearches = userData.recentSearches || [];
        const recentVehicle = recentSearches.find((v) => v.vin === vin);

        if (!recentVehicle) {
          console.log(
            `Vehicle not found locally, checking cache for VIN: ${vin}`
          );

          // Check if we already have this VIN in memory cache
          const cacheKey = `${vin}_${currentUser.uid}`;
          if (vehicleDataCache.has(cacheKey)) {
            console.log(`Using in-memory cached data for VIN: ${vin}`);
            const cachedResponse = vehicleDataCache.get(cacheKey);
            return {
              status: 200,
              data: {
                vin: vin,
                vehicleData: cachedResponse.data,
                report: {
                  generated: new Date().toISOString(),
                  source: 'Memory Cache',
                  data: cachedResponse.data,
                },
                metrics: cachedResponse.data?.metrics || {
                  overallScore: 0,
                  performance: 0,
                  value: 0,
                  reliability: 0,
                  safety: 0,
                  features: 0,
                  efficiency: 0,
                },
                recalls: cachedResponse.data?.recalls || [],
              },
            };
          }

          // Not found in local cache, delegating to getVehicleData
          console.log(
            `Not in memory cache either, delegating to getVehicleData for VIN: ${vin}`
          );
          // Use the existing getVehicleData method to avoid duplicate increments
          const apiResponse = await apiService.getVehicleData(
            vin,
            currentUser.uid
          );

          return {
            status: 200,
            data: {
              vin: vin,
              vehicleData: apiResponse.data,
              report: {
                generated: new Date().toISOString(),
                source: 'VIN Lookup Service',
                data: apiResponse.data,
              },
              metrics: apiResponse.data?.metrics || {
                overallScore: 0,
                performance: 0,
                value: 0,
                reliability: 0,
                safety: 0,
                features: 0,
                efficiency: 0,
              },
              recalls: apiResponse.data?.recalls || [],
            },
          };
        }

        // For recent searches, we use the cached API endpoint with a cache flag
        console.log(
          `🔄 Vehicle found in recent searches, making API call with cache flag`
        );
        return api
          .get(config.API.ENDPOINTS.VIN_LOOKUP, {
            params: {
              vin,
              userId: currentUser.uid,
              cached: true, // Add cache flag to signal this is a cache read
            },
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
          .then((response) => {
            // Add to local cache to prevent future duplicate calls
            const cacheKey = `${vin}_${currentUser.uid}`;
            vehicleDataCache.set(cacheKey, response);

            return {
              status: 200,
              data: {
                vin: vin,
                vehicleData: response.data,
                report: {
                  generated: new Date().toISOString(),
                  source: 'VIN Lookup Service (Cache)',
                  data: response.data,
                },
                metrics: response.data?.metrics || {
                  overallScore: 0,
                  performance: 0,
                  value: 0,
                  reliability: 0,
                  safety: 0,
                  features: 0,
                  efficiency: 0,
                },
                recalls: response.data?.recalls || [],
              },
            };
          });
      }

      console.log(
        `✅ Found vehicle in garage:`,
        vehicle.make,
        vehicle.model,
        vehicle.year
      );

      // Transform Firebase vehicle data to match expected format
      const transformedResponse = {
        status: 200,
        data: {
          vin: vin,
          vehicleData: vehicle,
          report: {
            generated: vehicle.lastUpdated || new Date().toISOString(),
            source: 'Firebase Cache',
            data: vehicle,
          },
          // Calculate or use existing metrics
          metrics: vehicle.metrics || {
            overallScore: calculateOverallScore(vehicle),
            performance: calculatePerformanceScore(vehicle),
            value: calculateValueScore(vehicle),
            reliability: getBrandReliabilityScore(vehicle.make),
            safety: calculateSafetyScore(vehicle),
            features: calculateFeaturesScore(vehicle),
            efficiency: calculateEfficiencyScore(vehicle),
          },
          recalls: vehicle.recalls || [],
        },
      };

      console.log(`🔄 Transformed Firebase data for VIN ${vin}`);
      return transformedResponse;
    } catch (error) {
      console.error(`❌ Firebase error for VIN ${vin}:`, error);

      // Fallback to API call if Firebase fails
      console.log(`🔄 Falling back to API call for VIN ${vin}`);

      try {
        // Use getVehicleData to prevent duplicate increments
        const userId = auth?.currentUser?.uid || '';
        const apiResponse = await apiService.getVehicleData(vin, userId);

        return {
          status: 200,
          data: {
            vin: vin,
            vehicleData: apiResponse.data,
            report: {
              generated: new Date().toISOString(),
              source: 'VIN Lookup Service (Fallback)',
              data: apiResponse.data,
            },
            metrics: apiResponse.data?.metrics || {
              overallScore: 0,
              performance: 0,
              value: 0,
              reliability: 0,
              safety: 0,
              features: 0,
              efficiency: 0,
            },
            recalls: apiResponse.data?.recalls || [],
          },
        };
      } catch (apiError) {
        console.error(`❌ API fallback also failed for VIN ${vin}:`, apiError);
        throw apiError;
      }
    }
  },

  /**
   * ============ PAYMENT ELEMENT METHODS (NEW) ============
   */

  /**
   * Create payment intent for Payment Element
   * Supports both authenticated users (with idToken) and guest checkout (with email)
   * @param {object} paymentData - Payment intent data
   * @param {number} paymentData.amount - Amount in cents
   * @param {string} paymentData.currency - Currency code (default: 'usd')
   * @param {number} paymentData.searchCredits - Number of search credits
   * @param {string} paymentData.productName - Product name
   * @param {string} paymentData.membershipType - Membership type
   * @param {number} paymentData.durationDays - Duration in days
   * @param {string} [paymentData.idToken] - Firebase authentication token (for authenticated users)
   * @param {string} [paymentData.email] - Email address (for guest checkout)
   * @param {string} [paymentData.password] - Password (for guest checkout account creation)
   * @param {object} paymentData.metadata - Additional metadata
   * @returns {Promise} Payment intent response with client secret
   */
  createPaymentIntent: async (paymentData) => {
    const isGuest = !paymentData.idToken && paymentData.email;

    console.log('Creating payment intent with data:', {
      amount: paymentData.amount,
      searchCredits: paymentData.searchCredits,
      productName: paymentData.productName,
      isGuest: isGuest,
      hasToken: !!paymentData.idToken,
      hasEmail: !!paymentData.email,
      hasCoupon: !!paymentData.couponCode,
    });

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add authorization header for authenticated users
    if (paymentData.idToken) {
      headers['Authorization'] = `Bearer ${paymentData.idToken}`;
      console.log('✅ Authorization header added (authenticated user)');
    } else if (paymentData.email) {
      console.log('✅ Guest checkout with email:', paymentData.email);
    } else {
      console.warn('⚠️ No idToken or email provided for payment request');
    }

    const requestBody = {
      amount: paymentData.amount,
      currency: paymentData.currency || 'usd',
      searchCredits: paymentData.searchCredits,
      productName: paymentData.productName,
      membershipType: paymentData.membershipType || 'premium',
      durationDays: paymentData.durationDays || 365,
      metadata: paymentData.metadata || {},
    };

    // Add email and password for guest checkout
    if (paymentData.email) {
      requestBody.email = paymentData.email;
    }

    if (paymentData.password) {
      requestBody.password = paymentData.password;
    }

    // Add coupon code if provided
    if (paymentData.couponCode) {
      requestBody.couponCode = paymentData.couponCode;
    }

    // Add referral code if provided
    if (paymentData.referralCode) {
      requestBody.referralCode = paymentData.referralCode;
    }

    try {
      const response = await api.post(
        '/api/create-payment-intent',
        requestBody,
        {
          headers,
        }
      );
      console.log(
        `✅ Payment intent created successfully (${isGuest ? 'guest' : 'authenticated'})`
      );
      return response;
    } catch (error) {
      console.error(
        '❌ Payment intent creation failed:',
        error.response?.status,
        error.response?.data
      );
      throw error;
    }
  },

  /**
   * Get payment intent status
   * @param {string} paymentIntentId - Payment intent ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Payment intent status response
   */
  getPaymentIntentStatus: async (paymentIntentId, idToken) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    return api.get(`/api/payment-intent/${paymentIntentId}`, { headers });
  },

  /**
   * Create customer portal session for subscription management
   * @param {string} returnUrl - URL to return to after portal session
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Portal session response with URL
   */
  createPortalSession: async (returnUrl, idToken) => {
    console.log('Creating customer portal session with return URL:', returnUrl);

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    const requestBody = {
      return_url: returnUrl,
    };

    return api.post('/api/create-portal-session', requestBody, { headers });
  },

  /**
   * Create a Stripe Connect Express account onboarding link for referral payouts.
   * @param {string} idToken - Firebase auth token
   * @returns {Promise<{url: string}>} Stripe-hosted onboarding URL
   */
  createStripeConnectLink: async (idToken) => {
    return api.post(
      '/api/referral/connect-stripe',
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
  },

  /**
   * Request a payout of the user's full referral credit balance to their
   * connected Stripe bank account. Minimum $20.
   * @param {string} idToken - Firebase auth token
   * @returns {Promise<{success, amount, transferId, message}>}
   */
  requestReferralPayout: async (idToken) => {
    return api.post(
      '/api/referral/payout',
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
  },

  /**
   * Check the status of the user's Stripe Connect account.
   * @param {string} idToken - Firebase auth token
   * @returns {Promise<{connected, payoutsEnabled, chargesEnabled}>}
   */
  getReferralConnectStatus: async (idToken) => {
    return api.get('/api/referral/connect-status', {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  /**
   * Fetch referral dashboard data for the authenticated user.
   * @param {string} idToken - Firebase auth token
   * @returns {Promise<{referralCode, referralCredits, totalEarned, totalPaidOut, referralsCount, referralHistory, payoutHistory}>}
   */
  getReferralDashboard: async (idToken) => {
    return api.get('/api/referral/dashboard', {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  /**
   * Handle payment failure logging
   * @param {string} paymentIntentId - Payment intent ID that failed
   * @param {object} errorDetails - Error details
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Failure logging response
   */
  handlePaymentFailure: async (paymentIntentId, errorDetails, idToken) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    const requestBody = {
      paymentIntentId,
      errorDetails,
      userAgent: navigator.userAgent,
    };

    try {
      return api.post('/api/handle-payment-failure', requestBody, { headers });
    } catch (error) {
      console.error('Failed to log payment failure:', error);
      // Don't throw error as this is just logging
      return { success: false };
    }
  },

  /**
   * Verify payment completion and ensure credits are allocated
   * @param {object} verificationData - Payment verification data
   * @param {string} verificationData.paymentIntentId - Payment intent ID
   * @param {string} verificationData.plan - Payment plan
   * @param {number} verificationData.credits - Number of credits purchased
   * @param {string} verificationData.coupon - Coupon code used (if any)
   * @returns {Promise} Payment verification response
   */
  verifyPayment: async (verificationData) => {
    const { paymentIntentId, plan, credits, coupon } = verificationData;

    console.log('Verifying payment with data:', {
      paymentIntentId,
      plan,
      credits,
      coupon,
    });

    const headers = {
      'Content-Type': 'application/json',
    };

    // Get user authentication token
    try {
      const { auth } = await import('../firebase');
      const { getIdToken } = await import('firebase/auth');

      if (auth.currentUser) {
        const idToken = await getIdToken(auth.currentUser);
        headers['Authorization'] = `Bearer ${idToken}`;
      } else {
        console.warn('⚠️ No authenticated user for payment verification');
      }
    } catch (error) {
      console.warn('⚠️ Failed to get auth token for verification:', error);
    }

    const requestBody = {
      paymentIntentId,
      plan,
      credits,
      coupon,
    };

    try {
      const response = await api.post('/api/verify-payment', requestBody, {
        headers,
      });
      console.log('✅ Payment verification successful');
      return response;
    } catch (error) {
      console.error(
        '❌ Payment verification failed:',
        error.response?.status,
        error.response?.data
      );
      throw error;
    }
  },

  /**
   * Get current user's credit status
   * @returns {Promise} User credit status response
   */
  getUserCredits: async () => {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Get user authentication token
    try {
      const { auth } = await import('../firebase');
      const { getIdToken } = await import('firebase/auth');

      if (auth.currentUser) {
        const idToken = await getIdToken(auth.currentUser);
        headers['Authorization'] = `Bearer ${idToken}`;
      } else {
        throw new Error('No authenticated user');
      }
    } catch (error) {
      console.error('❌ Failed to get auth token for credit check:', error);
      throw error;
    }

    try {
      const response = await api.get('/api/user/credits', { headers });
      console.log('✅ Credit status retrieved successfully');
      return response;
    } catch (error) {
      console.error(
        '❌ Failed to get user credits:',
        error.response?.status,
        error.response?.data
      );
      throw error;
    }
  },

  /**
   * ============ EXISTING PAYMENT METHODS (LEGACY SUPPORT) ============
   */

  /**
   * Stripe checkout session creation (Legacy - for backward compatibility)
   * @param {object} paymentData - Payment data for checkout
   * @returns {Promise} Checkout session response
   */
  createCheckoutSession: async (paymentData) => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (paymentData.idToken) {
      headers['Authorization'] = `Bearer ${paymentData.idToken}`;
    }

    // CRITICAL FIX: Ensure idToken is explicitly in the request body
    const requestBody = {
      price: paymentData.price,
      success_url: paymentData.successUrl || paymentData.success_url,
      cancel_url: paymentData.cancelUrl || paymentData.cancel_url,
      searchCredits: paymentData.searchCredits,
      productName: paymentData.productName,
      membershipType: paymentData.membershipType,
      durationDays: paymentData.durationDays,
      idToken: paymentData.idToken, // THIS IS THE KEY FIX
      metadata: paymentData.metadata || {},
      // Include referral code so the webhook can credit the referrer
      ...(paymentData.referralCode
        ? { referralCode: paymentData.referralCode }
        : {}),
    };

    return api.post(config.API.ENDPOINTS.PAYMENT.CHECKOUT, requestBody, {
      headers,
    });
  },

  /**
   * Create Stripe customer portal session (Legacy)
   * @param {object} portalData - Portal configuration data
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Portal session response
   */
  createPortalSessionLegacy: async (portalData, idToken) => {
    return api.post(config.API.ENDPOINTS.PAYMENT.PORTAL, portalData, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  },

  /**
   * Get payment details for a user - with multiple endpoint fallbacks
   * @param {string} userId - User ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Payment details response
   */
  getPaymentDetails: async (userId, idToken) => {
    const headers = { Authorization: `Bearer ${idToken}` };

    try {
      // Try multiple possible endpoint paths in sequence
      const possibleEndpoints = [
        `${config.API.ENDPOINTS.PAYMENT.DETAILS}?userId=${userId}`,
        `/api/payments/details?userId=${userId}`,
        `/api/payment?userId=${userId}`,
        `/api/users/payment?userId=${userId}`,
        `/api/getPaymentDetails?userId=${userId}`,
      ];

      return await tryMultipleEndpoints(possibleEndpoints, api, { headers });
    } catch (error) {
      console.error('All payment detail endpoints failed:', error);

      // Return default placeholder response to prevent UI errors
      return {
        data: {
          paymentHistory: [],
          lastPayment: null,
          membershipLevel: 'free',
          searchLimit: 5,
          searchesUsed: 0,
          expirationDate: null,
        },
      };
    }
  },

  /**
   * ============ USER MANAGEMENT METHODS ============
   */

  /**
   * Check user's search credits
   * @param {string} userId - User ID (deprecated, now uses auth token)
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Search credits response
   */
  checkSearchCredits: async (userId, idToken) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    try {
      // First try the new endpoint
      console.log('🔍 Checking credits with new endpoint...');
      const response = await api.get('/api/user/credits', { headers });
      console.log('✅ Credits retrieved successfully:', response.data);

      // Transform the response to match the expected format
      const data = response.data;
      return {
        data: {
          canSearch: (data.searchCredits || 0) > 0,
          remaining: data.searchCredits || 0,
          searchLimit: data.searchLimit || 0,
          searchesUsed: Math.max(
            0,
            (data.searchLimit || 0) - (data.searchCredits || 0)
          ),
          membership: data.membership || 'free',
          subscriptionStatus: data.subscriptionStatus || 'inactive',
          expirationDate: data.lastPaymentDate,
          userId: data.userId,
        },
      };
    } catch (error) {
      console.warn('⚠️ New endpoint failed, trying legacy endpoints...', error);

      // Fallback to legacy endpoints
      try {
        const possibleEndpoints = [
          `/api/checkSearchCredits?userId=${userId}`,
          `/api/users/check-credits?userId=${userId}`,
        ];

        return await tryMultipleEndpoints(possibleEndpoints, api, { headers });
      } catch (legacyError) {
        console.error('All search credits endpoints failed:', legacyError);

        // Return default placeholder to prevent UI errors
        return {
          data: {
            canSearch: true,
            remaining: 3,
            searchLimit: 5,
            searchesUsed: 2,
          },
        };
      }
    }
  },

  /**
   * Get user's search limit - with multiple endpoint fallbacks
   * @param {string} userId - User ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Search limit response
   */
  getSearchLimit: async (userId, idToken) => {
    const headers = { Authorization: `Bearer ${idToken}` };

    try {
      const possibleEndpoints = [
        `${config.API.ENDPOINTS.USER.SEARCH_LIMIT}?userId=${userId}`,
        `/api/users/search-limit?userId=${userId}`,
        `/api/user/limit?userId=${userId}`,
        `/api/getSearchLimit?userId=${userId}`,
      ];

      return await tryMultipleEndpoints(possibleEndpoints, api, { headers });
    } catch (error) {
      console.error('All search limit endpoints failed:', error);

      // Return default placeholder to prevent UI errors
      return {
        data: {
          searchLimit: 5,
        },
      };
    }
  },

  /**
   * Get user's searches used - with multiple endpoint fallbacks
   * @param {string} userId - User ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Searches used response
   */
  getSearchesUsed: async (userId, idToken) => {
    const headers = { Authorization: `Bearer ${idToken}` };

    try {
      const possibleEndpoints = [
        `${config.API.ENDPOINTS.USER.SEARCHES_USED}?userId=${userId}`,
        `/api/users/searches-used?userId=${userId}`,
        `/api/user/searches?userId=${userId}`,
        `/api/getSearchesUsed?userId=${userId}`,
      ];

      return await tryMultipleEndpoints(possibleEndpoints, api, { headers });
    } catch (error) {
      console.error('All searches used endpoints failed:', error);

      // As a last resort, try to get the user data directly from Firebase
      try {
        console.log('Attempting to get searches used from Firebase directly');
        // This requires Firebase to be available
        if (window.firebase && window.firebase.firestore) {
          const db = window.firebase.firestore();
          const userDoc = await db.collection('users').doc(userId).get();
          if (userDoc.exists) {
            const userData = userDoc.data();
            return {
              data: {
                searchesUsed: userData.searchesUsed || 0,
              },
            };
          }
        }
      } catch (firebaseError) {
        console.error('Firebase fallback also failed:', firebaseError);
      }

      // Return default placeholder to prevent UI errors
      return {
        data: {
          searchesUsed: 0,
        },
      };
    }
  },

  /**
   * Increment user's search count - with endpoint fallbacks
   * @param {string} userId - User ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Updated search count response
   */
  incrementSearchCount: async (userId, idToken) => {
    const headers = { Authorization: `Bearer ${idToken}` };

    try {
      const endpoints = [
        config.API.ENDPOINTS.USER.INCREMENT_SEARCH,
        '/api/users/increment-search',
        '/api/user/increment-search',
        '/api/incrementSearch',
      ];

      for (const endpoint of endpoints) {
        try {
          console.log(
            `Trying to increment search count with endpoint: ${endpoint}`
          );
          return await api.post(endpoint, { userId }, { headers });
        } catch (error) {
          console.warn(`Endpoint ${endpoint} failed: ${error.message}`);
          // Continue to next endpoint
        }
      }

      throw new Error('All increment search endpoints failed');
    } catch (error) {
      console.error('Failed to increment search count:', error);
      throw error;
    }
  },

  /**
   * Get user profile
   * @param {string} userId - User ID
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} User profile response
   */
  getUserProfile: async (userId, idToken) => {
    const headers = { Authorization: `Bearer ${idToken}` };

    try {
      const possibleEndpoints = [
        `${config.API.ENDPOINTS.USER.PROFILE}/${userId}`,
        `/api/users/profile/${userId}`,
        `/api/user/${userId}/profile`,
        `/api/getUserProfile/${userId}`,
      ];

      return await tryMultipleEndpoints(possibleEndpoints, api, { headers });
    } catch (error) {
      console.error('All user profile endpoints failed:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {object} profileData - Profile data to update
   * @param {string} idToken - Firebase authentication token
   * @returns {Promise} Update response
   */
  updateUserProfile: async (userId, profileData, idToken) => {
    return api.put(
      `${config.API.ENDPOINTS.USER.PROFILE}/${userId}`,
      profileData,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );
  },

  /**
   * ============ SYSTEM METHODS ============
   */

  /**
   * Get system health
   * @returns {Promise} Health check response
   */
  getSystemHealth: async () => {
    return api.get(config.API.ENDPOINTS.SYSTEM.HEALTH);
  },

  /**
   * Get API version
   * @returns {Promise} Version information response
   */
  getApiVersion: async () => {
    return api.get(config.API.ENDPOINTS.SYSTEM.VERSION);
  },

  /**
   * Test API connection
   * @returns {Promise} Connection test response with latency
   */
  testConnection: async () => {
    const startTime = Date.now();
    const response = await api.get(config.API.ENDPOINTS.SYSTEM.HEALTH);
    const latency = Date.now() - startTime;
    return {
      isConnected: true,
      latency,
      response: response.data,
    };
  },

  /**
   * ============ BLOG API METHODS ============
   */

  /**
   * Get blog posts with optional filtering
   * @param {string} query - Optional search query
   * @param {number} limit - Maximum number of posts to return
   * @param {string} category - Optional category filter
   * @returns {Promise} Blog posts response
   */
  getBlogPosts: async (query = '', limit = 5, category = '') => {
    try {
      const params = {
        query,
        limit: parseInt(limit),
      };

      if (category) {
        params.category = category;
      }

      const response = await blogApi.get(config.API.ENDPOINTS.BLOG.POSTS, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return { posts: [] };
    }
  },

  /**
   * Get a specific blog post by ID or slug
   * @param {string} idOrSlug - Blog post ID or slug
   * @returns {Promise} Blog post response
   */
  getBlogPostById: async (idOrSlug) => {
    try {
      const endpoint = config.API.ENDPOINTS.BLOG.POST_BY_ID.replace(
        ':id',
        idOrSlug
      );
      const response = await blogApi.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      return null;
    }
  },

  /**
   * Get related blog posts for a specific post
   * @param {string} postId - Blog post ID or slug
   * @param {number} limit - Maximum number of related posts to return
   * @returns {Promise} Related posts response
   */
  getRelatedBlogPosts: async (postId, limit = 3) => {
    try {
      const endpoint = config.API.ENDPOINTS.BLOG.RELATED.replace(':id', postId);
      const response = await blogApi.get(endpoint, {
        params: { limit: parseInt(limit) },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching related blog posts:', error);
      return [];
    }
  },

  /**
   * Get blog categories
   * @returns {Promise} Blog categories response
   */
  getBlogCategories: async () => {
    try {
      const response = await blogApi.get(config.API.ENDPOINTS.BLOG.CATEGORIES);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog categories:', error);
      return { categories: [] };
    }
  },

  /**
   * Get blog tags
   * @returns {Promise} Blog tags response
   */
  getBlogTags: async () => {
    try {
      const response = await blogApi.get(config.API.ENDPOINTS.BLOG.TAGS);
      return response.data;
    } catch (error) {
      console.error('Error fetching blog tags:', error);
      return { tags: [] };
    }
  },

  /**
   * Generic POST method for direct API calls
   * @param {string} endpoint - API endpoint path
   * @param {object} data - Request data
   * @param {object} options - Additional axios options
   * @returns {Promise} API response
   */
  post: async (endpoint, data = {}, options = {}) => {
    try {
      const response = await api.post(endpoint, data, options);
      return response;
    } catch (error) {
      console.error(`POST ${endpoint} error:`, error);
      throw error;
    }
  },
};

// Helper functions for vehicle metrics calculations
function calculateOverallScore(vehicle) {
  // Implementation depends on your metrics system
  return vehicle.metrics?.overallScore || 75;
}

function calculatePerformanceScore(vehicle) {
  // Implementation depends on your metrics system
  return vehicle.metrics?.performance || 70;
}

function calculateValueScore(vehicle) {
  // Implementation depends on your metrics system
  return vehicle.metrics?.value || 75;
}

function getBrandReliabilityScore(make) {
  // Basic implementation based on common reliability data
  if (!make) return 70;

  const reliableBrands = ['toyota', 'honda', 'lexus', 'mazda'];
  const goodBrands = ['subaru', 'acura', 'kia', 'hyundai', 'porsche'];
  const averageBrands = [
    'ford',
    'chevrolet',
    'gmc',
    'nissan',
    'volkswagen',
    'mini',
  ];

  const makeLower = make.toLowerCase();

  if (reliableBrands.includes(makeLower)) return 85;
  if (goodBrands.includes(makeLower)) return 80;
  if (averageBrands.includes(makeLower)) return 75;

  return 70; // default for other brands
}

function calculateSafetyScore(vehicle) {
  // Implementation depends on your metrics system
  return vehicle.metrics?.safety || 80;
}

function calculateFeaturesScore(vehicle) {
  // Implementation depends on your metrics system
  return vehicle.metrics?.features || 75;
}

function calculateEfficiencyScore(vehicle) {
  // Basic implementation based on fuel type
  const isElectric = vehicle.specifications?.fuelType
    ?.toLowerCase()
    ?.includes('electric');
  const isHybrid = vehicle.specifications?.fuelType
    ?.toLowerCase()
    ?.includes('hybrid');

  if (isElectric) return 95;
  if (isHybrid) return 85;

  return vehicle.metrics?.efficiency || 70;
}

export default apiService;
